import { type LocalizedText } from '@store/api/poiTypeEndpoints';
import { type ReportDesignContent, type ReportText } from '@store/api/reportDesignEndpoints';
import { type DesignImages } from '../ImageField';
import { DEFAULT_ACCENT } from '../options';
import { type Sample, type SampleSummary } from './sample';

/**
 * What the canvas takes from the renderer that draws a design as a PDF, so that a sheet here
 * can stand for a page there. Everything in this folder that is a colour, a size or a rule of
 * what a report prints is copied by hand from `backend/src/core/utils/report/pdf` (`blocks.tsx`,
 * `DesignReport.tsx`, `palette.ts`, `format.ts`), and changes when that does. The server's
 * PDF stays the truth: the canvas is close enough to compose on, and no closer.
 */

/** A4 in PDF points, the unit every size of a report is given in. */
export const A4 = { width: 595, height: 842 } as const;

/**
 * A length of so many PDF points as the sheet it is on draws it. A sheet sets `--pt`, the
 * pixels one point takes at its width (`usePointScale`), and everything on it is sized by
 * that. Not a scaled sheet: inside a `transform` the drag context moves what is dragged by
 * the wrong distance.
 */
export const pt = (points: number): string => `calc(var(--pt) * ${points})`;

/** The print palette: `palette` of `report/pdf/palette.ts`. */
export const palette = {
  ink: '#1A1F2E',
  muted: '#6B7280',
  faint: '#9CA3AF',
  hairline: '#E3E6EB',
  paper: '#FFFFFF',
  panel: '#F6F7F9',
  navy: '#16233A',
  accent: DEFAULT_ACCENT,
  status: { Ok: '#2E9E5B', Warning: '#D98E1F', Error: '#C94040', NotChecked: '#8A90A0' },
  statusTint: { Ok: '#E7F5EC', Warning: '#FBF1DF', Error: '#F9E7E7', NotChecked: '#EEEFF3' },
} as const;

/** The colours a block is drawn in: on a white page, and on a navy one (`ON_PAPER`, `ON_NAVY` of `blocks.tsx`). */
export interface Tone {
  ink: string;
  muted: string;
  faint: string;
  hairline: string;
}

export const toneOf = (dark: boolean): Tone => (dark
  ? { ink: palette.paper, muted: '#B9BDC4', faint: '#8B919D', hairline: '#505A6B' }
  : { ink: palette.ink, muted: palette.muted, faint: palette.faint, hairline: palette.hairline });

/**
 * Whether a report of the design has the page the results sheet stands for, by the renderer's
 * own rule for which pages there are (`flows`, `detailed` and `isBlank` in `DesignReport.tsx`).
 * The summary prints on a page between the covers and the closing pages, and so does every
 * template of the checkpoints. With both switched off there is no such page: the closing
 * pages follow the covers. A design that comes to no page at all that way is still printed
 * as one, empty but for its header and footer, and that is then the page the sheet stands for.
 *
 * `detailed` prints a page for each point it lists and none where it lists none. That does
 * not come up on the canvas: the sample has a checkpoint of every status, so no filter of a
 * design leaves it without a point.
 */
export const hasResultsPage = ({ covers, summary, checkpoints, closings }: ReportDesignContent): boolean =>
  summary.template !== 'none' || checkpoints.template !== 'none' || covers.length + closings.length === 0;

/** What a report prints where a value is missing. */
export const EMPTY_VALUE = '-';

/** A text with the language it is written in, which is what putting it in capitals goes by. */
export interface ResolvedText {
  text: string;
  locale: string;
}

/**
 * A text in capitals by the rules of its own language, as the renderer puts it: Turkish `i`
 * is `İ`, English `I`. Never a `text-transform`, which goes by the page's language: that is
 * the reader's, and a text found in another one would be cased by the wrong rules.
 */
export const capitals = ({ text, locale }: ResolvedText): string => {
  try {
    return text.toLocaleUpperCase(locale);
  } catch (_) {
    // A language tag the runtime does not recognise: the language-neutral rules.
    return text.toUpperCase();
  }
};

/** `t` of the localization hook. */
export type Translate = (key: string, values?: Array<string | number | boolean>) => string;

/**
 * What a sheet is drawn from: the sample inspection, and what the renderer works out once of
 * a design and its reader (`RenderContext` of `blocks.tsx`), in the interface language.
 */
export interface Print {
  /** The interface language, which stands for the language a report is asked for in. */
  language: string;
  sample: Sample;
  summary: SampleSummary;
  /** The moment the canvas was opened, where a report prints when it was made. */
  generatedAt: Date;
  /** The design's accent colour, or the palette's. */
  accent: string;
  /** The design's brand name for this reader, that of the reader's brand where it has none, or the product's. */
  brandName: string;
  /**
   * The design's logo as an address a picture can show; where it has none or it cannot be
   * read, the logo of the reader's brand, and the product's under that.
   */
  logo: string;
  /** The design's uploaded images, for a block that draws one. */
  images: DesignImages;
  t: Translate;
  /** An interface string with the language it was found in, for a label that goes in capitals. */
  translate: (key: string, values?: Array<string | number | boolean>) => ResolvedText;
  /** Text the author wrote, for this reader and with its placeholders filled. Null where there is none. */
  text: (value: ReportText | null | undefined) => ResolvedText | null;
  /** A label that travels with the data, as the sample's answers carry them. */
  label: (value: LocalizedText) => string;
  formatDate: (date: Date) => string;
  formatDateTime: (date: Date) => string;
  /** A number as the reader writes it, never grouped and never rounded: it is a recorded measurement. */
  formatNumber: (value: number) => string;
}

/**
 * The texts a design may ask the report to fill in, by the names it writes them under.
 * `contextOf` in `DesignReport.tsx` holds the list.
 */
export const placeholdersOf = (
  { sample, summary, generatedAt, formatDate }: Pick<Print, 'sample' | 'summary' | 'generatedAt' | 'formatDate'>,
): Map<string, string> => new Map([
  ['projectName', sample.projectName],
  ['inspectionName', sample.inspectionName],
  ['date', formatDate(sample.created)],
  ['generatedDate', formatDate(generatedAt)],
  ['operator', sample.operator || EMPTY_VALUE],
  ['completion', `${summary.completionPercent}%`],
]);

/**
 * A text with its placeholders filled, in one pass and with those names only, as the renderer
 * fills them: a value is put in as it is, so a `{date}` inside a project's name is not filled
 * a second time, and braces around anything else stay as the author wrote them.
 */
export const fillPlaceholders = (text: string, placeholders: ReadonlyMap<string, string>): string =>
  text.replace(/\{(\w+)\}/g, (placeholder, name: string) => placeholders.get(name) ?? placeholder);
